import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  Box,
  HStack,
  Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { AiExaminerPresence } from './AiExaminerPresence';
import {
  QUESTION_ARTICLE_STEPS,
  QuestionArticle,
  questionArticleStepForWidth,
  type QuestionArticleStep,
} from './QuestionArticle';

type QuestionHeroProps = {
  question?: string | null;
  showOrb?: boolean;
  /** Enables LiveKit voice-assistant wiring on the orb (cohort in-progress only). */
  activateVoiceAssistant?: boolean;
  /** Shrinks xl → lg → md to fit {@link viewportHeight} before scrolling (cohort). */
  adaptiveTextSize?: boolean;
  viewportHeight?: number | null;
};

const LAST_STEP_INDEX = QUESTION_ARTICLE_STEPS.length - 1;

type QuestionArticleStepState = {
  step: QuestionArticleStep;
  onWidth: (width: number) => void;
  onContentHeight: (height: number) => void;
};

function useQuestionArticleStep(
  questionText: string | undefined,
  viewportHeight: number | null | undefined,
  fitHeight: boolean,
): QuestionArticleStepState {
  const [width, setWidth] = useState<number | null>(null);
  const [heightIndex, setHeightIndex] = useState(0);
  const widthIndex = questionArticleStepForWidth(width);
  const index = Math.max(widthIndex, fitHeight ? heightIndex : 0);

  useEffect(() => {
    setHeightIndex(0);
  }, [fitHeight, questionText, viewportHeight, widthIndex]);

  const onContentHeight = useCallback((contentHeight: number) => {
    if (!fitHeight || viewportHeight == null || viewportHeight <= 0) return;
    if (contentHeight <= 0 || contentHeight <= viewportHeight) return;
    if (index >= LAST_STEP_INDEX) return;
    setHeightIndex(index + 1);
  }, [fitHeight, index, viewportHeight]);

  const onWidth = useCallback((next: number) => {
    setWidth((prev) => (prev === next ? prev : next));
  }, []);

  return { step: QUESTION_ARTICLE_STEPS[index], onWidth, onContentHeight };
}

/** Active exam question set as an article; optional orb for legacy mounts. */
export function QuestionHero({
  question,
  showOrb = true,
  activateVoiceAssistant = false,
  adaptiveTextSize = false,
  viewportHeight = null,
}: QuestionHeroProps) {
  const { t } = useTranslation();
  const questionText = question?.trim();
  const { step, onWidth, onContentHeight } = useQuestionArticleStep(
    questionText,
    viewportHeight,
    adaptiveTextSize,
  );

  return (
    <HStack
      testID="oral-exam-question-hero"
      space="xl"
      alignItems={showOrb ? 'center' : 'flex-start'}
      justifyContent="center"
      width="100%"
    >
      {showOrb ? (
        <AiExaminerPresence size={160} useLiveVoiceAssistant={activateVoiceAssistant} />
      ) : null}
      <Box
        flex={1}
        minWidth={0}
        onLayout={(event) => onWidth(event.nativeEvent.layout.width)}
      >
        {questionText ? (
          <Box
            key={step}
            testID={`oral-exam-question-text-size-${step}`}
            onLayout={(event) => onContentHeight(event.nativeEvent.layout.height)}
          >
            <QuestionArticle question={questionText} step={step} />
          </Box>
        ) : (
          <Text size="xl" fontFamily="$heading" color="$textHeading" lineHeight={36}>
            {t('oralExam.noCurrentQuestion', { defaultValue: 'No question active' })}
          </Text>
        )}
      </Box>
    </HStack>
  );
}
